
import Vue from 'vue';
import { Avatar, Icon, Button } from '@descomplica/vue-components';
import { UserModel } from '@descomplica/dex-models';
import { UnifiedCommentModel } from '@/models/UnifiedCommentModel';
import EditorPreview from '../EditorPreview.vue';
import CommentScore from './CommentScore.vue';
import CommentOptions from './CommentOptions.vue';
import DeleteCommentModal from '../modals/DeleteCommentModal.vue';
import CommentActions from './CommentActions.vue';
import FileContainer from '@/components/files/FileContainer.vue';
import { FileModel } from '../../models/FileModel';
import CommentInput from './CommentInput.vue';
import { AlumniProfileModel } from '@/models/alumni-profile/alumni-profile.model';
import i18n from '@/i18n';

interface AuthorData {
  name: string;
  photo: string;
}

export default Vue.extend({
  components: {
    Icon,
    Avatar,
    CommentScore,
    CommentOptions,
    DeleteCommentModal,
    CommentActions,
    EditorPreview,
    FileContainer,
    CommentInput,
    Button,
  },
  props: {
    isSending: {
      type: Boolean,
      default: false,
    },
    isPastDue: {
      type: Boolean,
      default: false,
    },
    showScore: {
      type: Boolean,
      default: false,
    },
    showRepliesCount: {
      type: Boolean,
      default: true,
    },
    showFeedbackButton: {
      type: Boolean,
      default: true,
    },
    showLikeAndDislikeButtons: {
      type: Boolean,
      default: true,
    },
    comment: {
      type: Object as Vue.PropType<UnifiedCommentModel>,
      required: true,
    },
    user: {
      type: Object as Vue.PropType<UserModel>,
      required: true,
    },
    enableCommentEdition: {
      type: Boolean,
      default: false,
    },
    remainingAttempts: {
      type: Number,
      default: null,
    },
    remainingAttemptsMessage: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isEditing: false as Boolean,
      isOptionsOpened: false as Boolean,
      isUpVotedComment: false as Boolean,
      isDeleteCommentModal: false as Boolean,
    };
  },
  computed: {
    currentAlumniProfile(): AlumniProfileModel {
      return this.$store.getters.currentAlumniProfile;
    },
    commentAttachments(): FileModel[] {
      const { attachments = [] } = this.comment;
      return attachments;
    },
    canEditComment(): boolean {
      const { score } = this.comment;
      const { remainingAttempts } = this;

      return (score === null || score === undefined) && remainingAttempts > 0;
    },
    isReply(): boolean {
      return !this.showRepliesCount;
    },
    author(): AuthorData {
      const defaultName = i18n.t('descomplicaStudent').toString();
      const defaultPhoto = '';

      if (this.comment && this.comment.userId !== this.user.id) {
        return {
          name: this.comment.userProfileName || defaultName,
          photo: this.comment.userProfileAvatar || defaultPhoto,
        };
      }

      return {
        name: this.currentAlumniProfile?.displayName || defaultName,
        photo: this.currentAlumniProfile?.avatarUrl || defaultPhoto,
      };
    },
    authorPhoto(): string {
      return this.author.photo;
    },
    authorName(): string {
      return this.author.name;
    },
    isUserAdmin(): boolean {
      return this.user.isAdmin();
    },
    isMyComment(): boolean {
      if (!this.comment || !this.comment.author) return false;
      return this.comment.userId === this.user.id;
    },
  },
  methods: {
    onCommentEdit(params: { text: string; files: FileModel[] }) {
      this.$emit('onCommentEdit', params);
    },
    toggleIsEditing() {
      this.isEditing = !this.isEditing;
      this.$emit('commentEditingCSSChange', this.isEditing);
    },
    toggleDeleteModal() {
      this.isDeleteCommentModal = !this.isDeleteCommentModal;
    },
    onScoreChange(score: number) {
      this.$emit('onScoreChange', { ...this.comment, score });
    },
    deleteForumInput() {
      if (this.isReply) {
        this.deleteReply();
      } else {
        this.deleteComment();
      }
    },
    deleteComment() {
      this.$emit('deleteComment', { ...this.comment });
    },
    deleteReply() {
      this.$emit('deleteReply', { ...this.comment });
    },
    onToggleReplies() {
      this.$emit('onToggleReplies');
    },
    onLikeClick(like: boolean) {
      this.$emit('onLikeClick', { comment: this.comment, like });
    },
    onDislikeClick(dislike: boolean) {
      this.$emit('onDislikeClick', { comment: this.comment, dislike });
    },
    onFeedbackClick() {
      this.$emit('onFeedbackClick');
    },
  },
});
